import { dragActivated, type DropOutcome, dropOutcome, type MapDropTarget } from "./listDrag";

/** Geste « glisser une unité depuis une liste vers la carte » (docs/14 §2.6), sans React ni DOM réel : la vue lui
 *  passe les événements pointeur de la ligne et reçoit, par son hôte, le fantôme à dessiner, la pose ou le retour
 *  du fantôme. Testé tel quel (listDragGesture.test.ts) avec une carte et des lignes simulées.
 *
 *  - Souris : toute la ligne glisse, au-delà de 4 px ; un simple clic (sans glisser) le dit.
 *  - Doigt ou stylet : seule la poignée (`data-drag-handle`) glisse, au-delà de 10 px (la liste garde son
 *    défilement).
 *  - Les boutons de la ligne (`data-row-action`) gardent leur clic.
 *  - Le pointeur n'est capturé qu'une fois le seuil passé : un simple clic reste celui du bouton de la ligne ; le
 *    clic qui suit un glissé est absorbé (`swallowClick`).
 *  - Dossier non enregistré : rien ne glisse, l'hôte désigne l'enregistrement.
 *  - Chaque geste a son numéro : le fantôme d'un geste n'est jamais retiré par la fin d'un geste précédent. */

/** Ce que le geste lit d'un événement pointeur. */
export interface GesturePointer {
  pointerId: number;
  pointerType: string;
  button: number;
  clientX: number;
  clientY: number;
  target: unknown;
}

/** Ligne source : elle capture le pointeur pendant le geste. */
export interface GestureRow {
  setPointerCapture?: (id: number) => void;
  releasePointerCapture?: (id: number) => void;
}

/** Unité glissée : `id` l'identifie dans la liste, `label` est écrit dans le fantôme. */
export interface DragItem {
  id: string;
  label: string;
  sidc: string;
}

/** Fantôme à dessiner : position du pointeur, point de la carte sous lui (null : hors de la carte) et, s'il y en a
 *  un, s'il tombe dans l'emprise admise du dossier. */
export interface GhostFrame {
  gesture: number;
  id: string;
  label: string;
  sidc: string;
  x: number;
  y: number;
  over: [number, number] | null;
  inside: boolean;
}

export type Refusal = Exclude<DropOutcome, { kind: "place" }>;

export interface ListDragHost<I extends DragItem, R extends GestureRow> {
  map: () => MapDropTarget | null;
  canEdit: () => boolean;
  refuseNotSaved: () => void;
  /** Fantôme déplacé (premier appel : le geste commence). */
  ghost: (frame: GhostFrame) => void;
  /** Dépôt accepté : fantôme retiré, unité posée en `at`. */
  drop: (item: I, at: [number, number], gesture: number) => void;
  /** Dépôt refusé (`refusal`, relâché en `x`, `y`) ou geste annulé (`refusal` null) : fantôme ramené à sa ligne. */
  back: (row: R, gesture: number, refusal: Refusal | null, at: { x: number; y: number } | null) => void;
  /** Simple clic à la souris, sans glisser. */
  click?: (row: R, item: I) => void;
}

interface Closest {
  closest?: (selector: string) => unknown;
}

function within(target: unknown, selector: string): boolean {
  const element = target as Closest | null;
  return Boolean(element && typeof element.closest === "function" && element.closest(selector));
}

export interface ListDragGesture<I extends DragItem, R extends GestureRow> {
  /** Vrai si le geste est pris en charge (le pointeur sera capturé au-delà du seuil). */
  down: (event: GesturePointer, row: R, item: I) => boolean;
  /** Vrai quand le geste est actif (l'appelant empêche alors le comportement par défaut). */
  move: (event: GesturePointer) => boolean;
  up: (event: GesturePointer) => void;
  cancel: (event: GesturePointer) => void;
  /** Geste en cours (pointeur pris), actif ou non. */
  pending: () => boolean;
  /** Vrai une fois, juste après un glissé : le clic que le navigateur émet au relâcher est à absorber. */
  swallowClick: () => boolean;
}

/** Délai pendant lequel le clic qui suit un glissé est absorbé. */
export const SWALLOW_CLICK_MS = 400;

export function createListDrag<I extends DragItem, R extends GestureRow>(host: ListDragHost<I, R>): ListDragGesture<I, R> {
  let state: {
    item: I;
    row: R;
    start: { x: number; y: number };
    touch: boolean;
    active: boolean;
    id: number;
    gesture: number;
  } | null = null;
  let gestures = 0;
  // Fin du dernier glissé (le clic émis au relâcher le suit de quelques millisecondes ; un clic au clavier, bien
  // plus tard, n'est jamais absorbé).
  let draggedAt = Number.NEGATIVE_INFINITY;

  const release = (event: GesturePointer) => {
    const current = state;
    if (!current || current.id !== event.pointerId) return null;
    state = null;
    if (current.active) {
      current.row.releasePointerCapture?.(event.pointerId);
      draggedAt = Date.now();
    }
    return current;
  };

  return {
    down(event, row, item) {
      if (event.pointerType === "mouse" && event.button !== 0) return false;
      if (within(event.target, "[data-row-action]")) return false;
      const touch = event.pointerType !== "mouse";
      if (touch && !within(event.target, "[data-drag-handle]")) return false;
      state = {
        item,
        row,
        start: { x: event.clientX, y: event.clientY },
        touch,
        active: false,
        id: event.pointerId,
        gesture: 0,
      };
      draggedAt = Number.NEGATIVE_INFINITY;
      return true;
    },

    move(event) {
      const current = state;
      if (!current || current.id !== event.pointerId) return false;
      const at = { x: event.clientX, y: event.clientY };
      if (!current.active) {
        if (!dragActivated(current.start, at, current.touch)) return false;
        if (!host.canEdit()) {
          release(event);
          host.refuseNotSaved();
          return false;
        }
        current.active = true;
        current.gesture = ++gestures;
        current.row.setPointerCapture?.(event.pointerId);
      }
      const map = host.map();
      const over = map?.locate(at.x, at.y) ?? null;
      host.ghost({
        gesture: current.gesture,
        id: current.item.id,
        label: current.item.label,
        sidc: current.item.sidc,
        x: at.x,
        y: at.y,
        over,
        inside: over ? Boolean(map?.inside(over)) : false,
      });
      return true;
    },

    up(event) {
      const current = release(event);
      if (!current) return;
      if (!current.active) {
        if (!current.touch) host.click?.(current.row, current.item);
        return;
      }
      const outcome = dropOutcome(host.map(), event.clientX, event.clientY);
      if (outcome.kind === "place") host.drop(current.item, outcome.at, current.gesture);
      else host.back(current.row, current.gesture, outcome, { x: event.clientX, y: event.clientY });
    },

    cancel(event) {
      const current = release(event);
      if (current?.active) host.back(current.row, current.gesture, null, null);
    },

    pending: () => state !== null,

    swallowClick() {
      const swallow = Date.now() - draggedAt < SWALLOW_CLICK_MS;
      draggedAt = Number.NEGATIVE_INFINITY;
      return swallow;
    },
  };
}

/** Fantôme après la fin du geste `gesture` (retour terminé, pose) : retiré s'il est encore celui de ce geste ; celui
 *  d'un geste suivant, commencé pendant le retour, est gardé. */
export function ghostAfterEnd<G extends { gesture: number }>(current: G | null, gesture: number): G | null {
  return current && current.gesture === gesture ? null : current;
}

/** Avis d'un dépôt refusé (« action bloquée → action attendue désignée ») :
 *  - hors de l'emprise du dossier : « Hors du camp » (ou « Hors du terrain »), « Cadrer sur le camp » proposé et
 *    désigné s'il est actif ;
 *  - hors de la carte, relâché au-dessus de la liste d'origine : geste abandonné, aucun avis ;
 *  - hors de la carte ailleurs (panneau, barre) : avis neutre, la carte est l'action attendue.
 *  Geste annulé par le système (`refusal` null) : aucun avis. */
export type RefusalNotice =
  | { kind: "zone"; free: boolean; designateFit: boolean }
  | { kind: "map" }
  | null;

export function refusalNotice(
  refusal: Refusal | null,
  context: { overSource: boolean; free: boolean; canFit: boolean },
): RefusalNotice {
  if (!refusal) return null;
  if (refusal.kind === "outside-zone") return { kind: "zone", free: context.free, designateFit: context.canFit };
  return context.overSource ? null : { kind: "map" };
}
